import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  ImageOff,
  Upload,
  Camera,
  CheckCircle2,
  XCircle,
  Loader2,
  MapPin,
  X,
  ChevronRight,
  ChevronLeft,
  Ban,
  Inbox,
  Copy as CopyIcon,
} from "lucide-react";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import catalog from "@/api/catalog";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
const PAGE_SIZE = 50;
const ACCEPTED_MIMES = ["image/jpeg", "image/jpg", "image/png", "image/webp", "image/heic", "image/heif"];
const MAX_BYTES = 10 * 1024 * 1024;

/**
 * Phase 5.7 — Bulk photo-upload mode.
 *
 * Activated via the CTA banner on `/catalog?photos=missing`. Renders a dense
 * grid of cards (4-6 columns), each with an inline drop zone + the SKU's
 * physical location (when GENQTY provides it).
 *
 * Supports multi-file drag-and-drop over the whole grid: filenames matching
 * a known SKU are auto-assigned; the rest land in an "unassigned" drawer
 * where the user picks the destination manually.
 */
export default function BulkPhotoUploadMode({ onExit }) {
  const [products, setProducts] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [uploadsInFlight, setUploadsInFlight] = useState({});  // sku → progress %
  const [uploadErrors, setUploadErrors] = useState({});         // sku → error
  const [sessionUploaded, setSessionUploaded] = useState(0);
  const [unassigned, setUnassigned] = useState([]);             // dropped files we can't match
  const [drawerOpen, setDrawerOpen] = useState(false);
  const dragCounter = useRef(0);
  const [draggingOver, setDraggingOver] = useState(false);

  // ---- Data loading -------------------------------------------------------
  const fetchPage = useCallback(async () => {
    setLoading(true);
    try {
      const r = await catalog.products({
        has_photo: false,
        photo_not_required: false,
        page,
        page_size: PAGE_SIZE,
        sort_by: "capital_tied_up",
        sort_dir: "desc",
      });
      setProducts(r.data.items || []);
      setTotal(r.data.total || 0);
      // Reset transient state for the new page.
      setUploadsInFlight({});
      setUploadErrors({});
    } catch (e) {
      toast.error("Error cargando productos sin foto");
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => {
    fetchPage();
  }, [fetchPage]);

  const skuSet = useMemo(() => new Set(products.map((p) => p.sku.toUpperCase())), [products]);
  const skuList = useMemo(() => products.map((p) => p.sku), [products]);

  // ---- Upload pipeline ----------------------------------------------------
  const validate = (file) => {
    if (file.size > MAX_BYTES) {
      return `Archivo > 10MB (${(file.size / 1024 / 1024).toFixed(1)}MB)`;
    }
    if (file.type && !ACCEPTED_MIMES.includes(file.type)) {
      return `Formato no soportado: ${file.type}`;
    }
    return null;
  };

  const uploadOne = async (sku, file) => {
    const err = validate(file);
    if (err) {
      setUploadErrors((m) => ({ ...m, [sku]: err }));
      toast.error(`${sku}: ${err}`);
      return;
    }
    setUploadErrors((m) => {
      const { [sku]: _drop, ...rest } = m;
      return rest;
    });
    setUploadsInFlight((m) => ({ ...m, [sku]: 1 }));
    try {
      const r = await catalog.uploadPhoto(sku, file, (e) => {
        if (!e.total) return;
        const pct = Math.round((e.loaded * 100) / e.total);
        setUploadsInFlight((m) => ({ ...m, [sku]: pct }));
      });
      toast.success(`✓ ${sku} actualizado`);
      setSessionUploaded((n) => n + 1);
      // Patch the card with the new URL — we don't remove it so the user
      // can keep replacing or undo if they spotted a typo.
      setProducts((prev) =>
        prev.map((p) =>
          p.sku === sku
            ? {
                ...p,
                local_image_url: r.data.local_image_url,
                has_photo: true,
                photo_uploaded_at: r.data.uploaded_at,
              }
            : p
        )
      );
      setUploadsInFlight((m) => {
        const { [sku]: _drop, ...rest } = m;
        return rest;
      });
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Error";
      setUploadErrors((m) => ({ ...m, [sku]: msg }));
      setUploadsInFlight((m) => {
        const { [sku]: _drop, ...rest } = m;
        return rest;
      });
      toast.error(`${sku}: ${msg}`);
    }
  };

  const markNotRequired = async (sku) => {
    try {
      await catalog.markPhotoNotRequired(sku);
      toast.success(`${sku} marcado como N/A`);
      setProducts((prev) => prev.filter((p) => p.sku !== sku));
      setTotal((t) => Math.max(0, t - 1));
    } catch {
      toast.error(`No se pudo marcar ${sku}`);
    }
  };

  // ---- Auto-assignment logic ---------------------------------------------
  const detectSkuForFile = (filename) => {
    const up = filename.toUpperCase().replace(/\.[A-Z0-9]+$/i, "");
    // Try direct substring match — the SKU appearing anywhere in the name.
    for (const sku of skuList) {
      if (up.includes(sku.toUpperCase())) return sku;
    }
    return null;
  };

  const handleDroppedFiles = (files) => {
    const unmatched = [];
    let matched = 0;
    Array.from(files).forEach((file) => {
      const sku = detectSkuForFile(file.name);
      if (sku) {
        matched += 1;
        uploadOne(sku, file);
      } else {
        unmatched.push(file);
      }
    });
    if (unmatched.length > 0) {
      setUnassigned((prev) => [...prev, ...unmatched]);
      setDrawerOpen(true);
    }
    if (matched > 0) {
      toast.success(`${matched} archivo(s) auto-asignado(s)`);
    }
  };

  // ---- Drag handlers (whole-grid level) -----------------------------------
  const onDragEnter = (e) => {
    e.preventDefault();
    dragCounter.current += 1;
    if (e.dataTransfer.types.includes("Files")) setDraggingOver(true);
  };
  const onDragLeave = () => {
    dragCounter.current -= 1;
    if (dragCounter.current <= 0) {
      dragCounter.current = 0;
      setDraggingOver(false);
    }
  };
  const onDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "copy";
  };
  const onDrop = (e) => {
    e.preventDefault();
    dragCounter.current = 0;
    setDraggingOver(false);
    if (e.dataTransfer.files?.length > 0) {
      handleDroppedFiles(e.dataTransfer.files);
    }
  };

  // ---- Render -------------------------------------------------------------
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div
      onDragEnter={onDragEnter}
      onDragLeave={onDragLeave}
      onDragOver={onDragOver}
      onDrop={onDrop}
      className="relative space-y-3"
      data-testid="bulk-photo-mode"
    >
      {/* Header with session counter + exit */}
      <div className="sticky top-0 z-10 -mx-2 flex flex-wrap items-center gap-3 rounded-lg border border-primary/30 bg-card/95 px-3 py-2 shadow-sm backdrop-blur">
        <Camera className="h-4 w-4 text-primary" />
        <div className="flex-1">
          <div className="text-sm font-semibold">Modo carga rápida de fotos</div>
          <div className="text-[11px] text-muted-foreground">
            Arrastra varias fotos a la vez — auto-asignamos por SKU en el nombre del archivo.
          </div>
        </div>
        <Badge
          variant="outline"
          className="font-mono text-xs"
          data-testid="bulk-session-counter"
        >
          Cargadas: {sessionUploaded} / {total} restantes
        </Badge>
        {unassigned.length > 0 && (
          <Button
            size="sm"
            variant="outline"
            onClick={() => setDrawerOpen(true)}
            data-testid="bulk-open-unassigned"
            className="border-amber-500/50 text-amber-600 dark:text-amber-400"
          >
            <Inbox className="mr-1 h-3.5 w-3.5" />
            {unassigned.length} sin asignar
          </Button>
        )}
        <Button size="sm" variant="ghost" onClick={onExit} data-testid="bulk-exit-btn">
          <X className="mr-1 h-3 w-3" />
          Salir del modo
        </Button>
      </div>

      {/* Drag overlay (visible during a drag) */}
      {draggingOver && (
        <div
          className="pointer-events-none fixed inset-0 z-50 flex items-center justify-center bg-primary/20 backdrop-blur-sm"
          data-testid="bulk-drag-overlay"
        >
          <div className="rounded-2xl border-2 border-dashed border-primary bg-card/95 px-12 py-8 text-center shadow-xl">
            <Upload className="mx-auto h-10 w-10 text-primary" />
            <div className="mt-2 text-base font-semibold">Suelta tus fotos aquí</div>
            <div className="text-xs text-muted-foreground">
              Auto-asignamos por SKU si está en el nombre.
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex h-64 items-center justify-center text-sm text-muted-foreground">
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          Cargando…
        </div>
      ) : products.length === 0 ? (
        <div
          className="rounded-xl border border-dashed border-emerald-500/40 bg-emerald-500/5 p-10 text-center"
          data-testid="bulk-empty"
        >
          <CheckCircle2 className="mx-auto h-10 w-10 text-emerald-500" />
          <div className="mt-2 text-base font-semibold">
            ¡No quedan productos sin foto en esta página!
          </div>
          <div className="text-xs text-muted-foreground">
            Pasa a la siguiente página o sal del modo carga.
          </div>
        </div>
      ) : (
        <div
          className="grid gap-3 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5"
          data-testid="bulk-grid"
        >
          {products.map((p) => (
            <BulkPhotoCard
              key={p.sku}
              p={p}
              progress={uploadsInFlight[p.sku]}
              error={uploadErrors[p.sku]}
              onPick={(file) => uploadOne(p.sku, file)}
              onMarkNA={() => markNotRequired(p.sku)}
            />
          ))}
        </div>
      )}

      {/* Pagination */}
      {total > PAGE_SIZE && (
        <div
          className="flex items-center justify-between border-t border-border pt-2"
          data-testid="bulk-pagination"
        >
          <div className="text-[11px] text-muted-foreground">
            Página {page} de {totalPages.toLocaleString("es-MX")} ·{" "}
            {total.toLocaleString("es-MX")} productos sin foto
          </div>
          <div className="flex gap-1.5">
            <Button
              size="sm"
              variant="outline"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              data-testid="bulk-prev-page"
            >
              <ChevronLeft className="mr-1 h-3 w-3" />
              Anterior
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => p + 1)}
              data-testid="bulk-next-page"
            >
              Siguiente
              <ChevronRight className="ml-1 h-3 w-3" />
            </Button>
          </div>
        </div>
      )}

      {/* Unassigned files drawer */}
      <UnassignedDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        files={unassigned}
        skuOptions={skuList}
        onAssign={(file, sku) => {
          uploadOne(sku, file);
          setUnassigned((prev) => prev.filter((f) => f !== file));
        }}
        onDiscard={(file) => {
          setUnassigned((prev) => prev.filter((f) => f !== file));
        }}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Individual card
// ---------------------------------------------------------------------------
function BulkPhotoCard({ p, progress, error, onPick, onMarkNA }) {
  const fileInputRef = useRef(null);
  const [hover, setHover] = useState(false);

  const hasUploaded = !!p.local_image_url;
  const photoUrl = hasUploaded ? `${BACKEND}${p.local_image_url}` : null;
  const location = p.genqty_locations?.[0];

  const handleFile = (file) => {
    if (file) onPick(file);
  };

  return (
    <Card
      data-testid={`bulk-card-${p.sku}`}
      className={cn(
        "relative overflow-hidden transition-all",
        error && "border-red-500/60",
        hasUploaded && "border-emerald-500/60"
      )}
    >
      <CardContent className="space-y-2 p-2.5">
        <div className="flex items-start justify-between gap-1.5">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1">
              <span
                className="truncate font-mono text-xs font-semibold"
                title={p.sku}
                data-testid={`bulk-sku-${p.sku}`}
              >
                {p.sku}
              </span>
              <button
                type="button"
                onClick={() => navigator.clipboard?.writeText(p.sku)}
                className="text-muted-foreground hover:text-primary"
                title="Copiar SKU"
              >
                <CopyIcon className="h-2.5 w-2.5" />
              </button>
            </div>
            <div
              className="line-clamp-2 text-[11px] leading-tight text-muted-foreground"
              title={p.name}
            >
              {p.name || "Sin nombre"}
            </div>
            {(p.vendor || p.genqty_brand) && (
              <div className="mt-0.5 truncate text-[10px] text-muted-foreground/70">
                {p.vendor || p.genqty_brand}
              </div>
            )}
          </div>
        </div>

        {location && (
          <div
            className="inline-flex items-center gap-1 rounded bg-amber-500/10 px-1.5 py-0.5 text-[10px] font-medium text-amber-600 dark:text-amber-400"
            data-testid={`bulk-loc-${p.sku}`}
          >
            <MapPin className="h-2.5 w-2.5" />
            {location}
          </div>
        )}

        {/* Drop zone / thumbnail */}
        <div
          onMouseEnter={() => setHover(true)}
          onMouseLeave={() => setHover(false)}
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => {
            e.preventDefault();
            e.stopPropagation();
            if (e.dataTransfer.files?.[0]) handleFile(e.dataTransfer.files[0]);
          }}
          onClick={() => fileInputRef.current?.click()}
          data-testid={`bulk-dropzone-${p.sku}`}
          className={cn(
            "relative flex aspect-square cursor-pointer items-center justify-center overflow-hidden rounded-md border-2 border-dashed transition-colors",
            hasUploaded
              ? "border-emerald-500/30 bg-emerald-500/5"
              : error
              ? "border-red-500/40 bg-red-500/5"
              : hover
              ? "border-primary bg-primary/5"
              : "border-border bg-muted/30"
          )}
        >
          {progress !== undefined ? (
            <div className="w-full px-3">
              <Loader2 className="mx-auto mb-2 h-5 w-5 animate-spin text-primary" />
              <Progress value={progress} className="h-1" />
              <div className="mt-1 text-center text-[10px] text-muted-foreground">
                Subiendo {progress}%
              </div>
            </div>
          ) : photoUrl ? (
            <>
              <img
                src={photoUrl}
                alt={p.sku}
                className="h-full w-full object-cover"
                data-testid={`bulk-thumb-${p.sku}`}
              />
              {hover && (
                <div className="absolute inset-0 flex items-center justify-center bg-black/60 text-[10px] font-medium text-white">
                  Reemplazar
                </div>
              )}
            </>
          ) : error ? (
            <div className="px-2 text-center text-[10px] text-red-500">
              <XCircle className="mx-auto mb-1 h-5 w-5" />
              {error}
            </div>
          ) : (
            <div className="px-2 text-center text-[10px] text-muted-foreground">
              <Upload className="mx-auto mb-1 h-5 w-5" />
              Soltar o click
            </div>
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept={ACCEPTED_MIMES.join(",")}
            className="hidden"
            onChange={(e) => {
              if (e.target.files?.[0]) handleFile(e.target.files[0]);
              e.target.value = "";
            }}
            data-testid={`bulk-file-input-${p.sku}`}
          />
        </div>

        <div className="flex items-center justify-between gap-1 text-[10px]">
          {hasUploaded ? (
            <span className="inline-flex items-center gap-0.5 text-emerald-600 dark:text-emerald-400">
              <CheckCircle2 className="h-2.5 w-2.5" />
              Subida
            </span>
          ) : (
            <span className="text-muted-foreground">{(p.qty_in_stock || 0)} en stock</span>
          )}
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onMarkNA();
            }}
            data-testid={`bulk-mark-na-${p.sku}`}
            className="inline-flex items-center gap-0.5 rounded px-1 text-muted-foreground hover:bg-muted hover:text-foreground"
            title="Marcar que no necesita foto"
          >
            <Ban className="h-2.5 w-2.5" />
            N/A
          </button>
        </div>
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Unassigned-files manual-assignment drawer
// ---------------------------------------------------------------------------
function UnassignedDrawer({ open, onClose, files, skuOptions, onAssign, onDiscard }) {
  const [picks, setPicks] = useState({});

  useEffect(() => {
    if (!open) setPicks({});
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        className="max-w-2xl"
        data-testid="bulk-unassigned-drawer"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Inbox className="h-4 w-4" />
            Asignar fotos manualmente
          </DialogTitle>
          <DialogDescription>
            No pudimos detectar un SKU en estos nombres de archivo. Elige a qué SKU pertenece cada uno.
          </DialogDescription>
        </DialogHeader>

        {files.length === 0 ? (
          <div className="py-6 text-center text-sm text-muted-foreground">
            No quedan archivos sin asignar.
          </div>
        ) : (
          <div className="max-h-[60vh] space-y-2 overflow-auto">
            {files.map((file, i) => {
              const url = URL.createObjectURL(file);
              return (
                <div
                  key={`${file.name}-${i}`}
                  className="flex items-center gap-3 rounded-md border border-border bg-muted/30 p-2"
                  data-testid={`unassigned-row-${i}`}
                >
                  <img
                    src={url}
                    alt={file.name}
                    onLoad={() => URL.revokeObjectURL(url)}
                    className="h-12 w-12 rounded object-cover"
                  />
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-xs font-medium">{file.name}</div>
                    <div className="text-[10px] text-muted-foreground">
                      {(file.size / 1024).toFixed(0)} KB · {file.type}
                    </div>
                  </div>
                  <input
                    type="text"
                    list="bulk-sku-options"
                    placeholder="Buscar SKU…"
                    value={picks[i] || ""}
                    onChange={(e) => setPicks((p) => ({ ...p, [i]: e.target.value }))}
                    data-testid={`unassigned-input-${i}`}
                    className="w-36 rounded border border-border bg-background px-2 py-1 font-mono text-xs"
                  />
                  <Button
                    size="sm"
                    disabled={!picks[i]}
                    onClick={() => onAssign(file, picks[i])}
                    data-testid={`unassigned-assign-${i}`}
                  >
                    Asignar
                  </Button>
                  <button
                    type="button"
                    onClick={() => onDiscard(file)}
                    data-testid={`unassigned-discard-${i}`}
                    className="rounded p-1 text-muted-foreground hover:bg-red-500/10 hover:text-red-500"
                    title="Descartar"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              );
            })}
            <datalist id="bulk-sku-options">
              {skuOptions.map((s) => (
                <option key={s} value={s} />
              ))}
            </datalist>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" size="sm" onClick={onClose} data-testid="unassigned-close-btn">
            Cerrar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
